import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, X, Eye, EyeOff } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

interface CreatePasswordStepProps {
  onPasswordCreated: () => void;
  isLoading: boolean;
}

interface PasswordRequirement {
  id: string;
  label: string;
  regex: RegExp;
  met: boolean;
}

export const CreatePasswordStep: React.FC<CreatePasswordStepProps> = ({
  onPasswordCreated,
  isLoading: parentLoading
}) => {
  const { toast } = useToast();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const requirements: PasswordRequirement[] = [
    { id: 'length', label: 'At least 8 characters', regex: /.{8,}/, met: false },
    { id: 'lowercase', label: 'One lowercase letter (a-z)', regex: /[a-z]/, met: false },
    { id: 'uppercase', label: 'One uppercase letter (A-Z)', regex: /[A-Z]/, met: false },
    { id: 'number', label: 'One number (0-9)', regex: /[0-9]/, met: false },
    { id: 'special', label: 'One special character (!@#$%^&*)', regex: /[!@#$%^&*(),.?":{}|<>]/, met: false },
  ];

  const updatedRequirements = requirements.map(req => ({
    ...req,
    met: req.regex.test(password)
  }));

  const allRequirementsMet = updatedRequirements.every(req => req.met);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allRequirementsMet) return;

    setIsLoading(true);
    try {
      // Validate password on backend first
      const { data: validationResult } = await supabase.functions.invoke('validate-password', {
        body: { password }
      });

      if (!validationResult?.isValid) {
        throw new Error(validationResult?.errors?.[0] || "Password validation failed");
      }

      // Update user password and clear password reset requirement
      const { error } = await supabase.auth.updateUser({
        password: password,
        data: { 
          requires_password_reset: false,
          has_password: true 
        }
      });

      if (error) {
        // Backend validation failed
        if (error.message.includes('Password should be')) {
          throw new Error("Password does not meet security requirements. Please ensure it contains at least 8 characters with uppercase, lowercase, number and special character.");
        }
        throw error;
      }

      toast({
        title: "Password created!",
        description: "Your account is almost ready.",
      });

      onPasswordCreated();
    } catch (error: any) {
      console.error('Password creation error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loading = isLoading || parentLoading;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="password" className="text-sm font-medium">
          Create a password
        </Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter your new password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="pr-10"
            required
            autoFocus
            aria-describedby="password-help password-requirements"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-0 flex items-center pr-3"
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Eye className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        </div>
        <p id="password-help" className="text-sm text-muted-foreground">
          You'll use this to sign in on this device and others.
        </p>
      </div>

      <div className="space-y-3">
        <p className="text-sm font-medium text-foreground">Password requirements:</p>
        <div id="password-requirements" className="space-y-2" role="list" aria-live="polite">
          {updatedRequirements.map((req) => (
            <div key={req.id} className="flex items-center gap-2 text-sm" role="listitem">
              {req.met ? (
                <Check className="h-4 w-4 text-green-500 flex-shrink-0" aria-label="Requirement met" />
              ) : (
                <X className="h-4 w-4 text-muted-foreground flex-shrink-0" aria-label="Requirement not met" />
              )}
              <span className={req.met ? 'text-green-600' : 'text-muted-foreground'}>
                {req.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <Button 
        type="submit" 
        className="w-full" 
        disabled={!allRequirementsMet || loading}
      >
        {loading ? 'Creating password...' : 'Create password'}
      </Button>
    </form>
  );
};